import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../store/store';
import { useBotContext } from '../store/BotContext';
import { useChatContext } from '../../bot-core/store/ChatContext';
import { createSession, loadSession, updateSessionContext, archiveSession, getCheckoutPayload } from '../api/botApi';
import { ItineraryItem, CheckoutPayload } from '../types/bot.types';
import { UIMessage } from '../../bot-core/types/chat.types';

/** Visible tab strip entry — one per session the user has open in this
 *  widget. The full session state lives on the server; tabs are local
 *  pointers. Scaffold for the multi-session UX requested by the user; only
 *  the active tab's chat is "live" at a time. */
export interface BotTab {
  id: string;       // session id
  title?: string;   // session title from BE (falls back to placeholder in UI)
}

function sessionKey(tenantId?: number | string, websiteId?: number | string) {
  return `sky360_bot_session_${tenantId ?? 'x'}_${websiteId ?? 'x'}`;
}
function tabsKey(tenantId?: number | string, websiteId?: number | string) {
  return `sky360_bot_tabs_${tenantId ?? 'x'}_${websiteId ?? 'x'}`;
}

interface PersistedTabs {
  tabs: BotTab[];
  activeId: string | null;
}

function readPersistedTabs(key: string): PersistedTabs {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return { tabs: [], activeId: null };
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.tabs)) return { tabs: [], activeId: null };
    return { tabs: parsed.tabs, activeId: parsed.activeId ?? null };
  } catch {
    return { tabs: [], activeId: null };
  }
}

/** Events365 lang — same shape B2C sends on /event/search (en_us, he_il, …). */
function toBotSearchLanguage(currentLang?: string): string {
  return (currentLang ?? 'en_US').toLowerCase();
}

export function useBotSession() {
  const { botState, botDispatch } = useBotContext();
  const { dispatch: chatDispatch } = useChatContext();

  const membershipUser = useSelector((s: RootState) => s.config.membershipUser) as any;
  const currentLang = useSelector((s: RootState) => s.config.currentLang) as string | undefined;

  const tenantId = Number(process.env.REACT_APP_TENANT_ID) || undefined;
  const websiteId = Number(process.env.REACT_APP_WEBSITE_ID) || undefined;
  const sKey = sessionKey(tenantId, websiteId);
  const tKey = tabsKey(tenantId, websiteId);

  const [tabs, setTabs] = useState<BotTab[]>(() => readPersistedTabs(tKey).tabs);
  const [activeTabId, setActiveTabId] = useState<string | null>(
    () => readPersistedTabs(tKey).activeId,
  );

  // Persist tabs on any change so the strip survives reload.
  useEffect(() => {
    try {
      localStorage.setItem(
        tKey,
        JSON.stringify({ tabs, activeId: activeTabId } as PersistedTabs),
      );
    } catch {
      // localStorage quota errors are non-fatal — the strip will rebuild
      // on next session.
    }
  }, [tabs, activeTabId, tKey]);

  // Keep session search.language in sync when the user switches site language.
  useEffect(() => {
    const sessionId = botState.sessionId;
    if (!sessionId || !currentLang) return;
    const lang = toBotSearchLanguage(currentLang);
    updateSessionContext(sessionId, {
      search: { language: lang },
    }).catch((e) => console.error('[BotSession] sync language', e));
  }, [currentLang, botState.sessionId]);

  const refreshSelections = useCallback(async (sessionId: string) => {
    try {
      const session = await loadSession(sessionId);
      const raw = session.context?.cart ?? session.context?.selections ?? [];
      const items: ItineraryItem[] = raw.map((s) => ({
        selection_id: ('cart_item_id' in s ? s.cart_item_id : s.selection_id) as string,
        product_type: s.product_type,
        display_name: s.display_name,
        display_summary: s.display_summary,
        price: s.price,
        currency: s.currency,
      }));
      const total = items.reduce((sum, i) => sum + Number(i?.price), 0);
      const currency = items[0]?.currency ?? 'USD';
      botDispatch({ type: 'SET_SELECTIONS', selections: items, totalPrice: total, currency });
    } catch (e) {
      console.error('[BotSession] refreshSelections', e);
    }
  }, [botDispatch]);

  const applyCheckoutPayload = useCallback(
    (payload: CheckoutPayload | null | undefined, expiresAt?: string | null) => {
      if (!payload?.itinerary_display?.length) {
        botDispatch({ type: 'CLEAR_CHECKOUT' });
        botDispatch({ type: 'SET_SELECTIONS', selections: [], totalPrice: 0, currency: 'USD' });
        return;
      }
      const items: ItineraryItem[] = payload.itinerary_display.map((item) => ({
        selection_id: item.cart_item_id,
        product_type: item.product_type,
        display_name: item.display_name,
        display_summary: item.display_summary,
        price: item.price,
        currency: item.currency,
      }));
      botDispatch({
        type: 'SET_SELECTIONS',
        selections: items,
        totalPrice: Number(payload.total_estimated_price),
        currency: payload.currency,
      });
      botDispatch({
        type: 'SET_CHECKOUT',
        payload,
        ...(expiresAt !== undefined ? { expiresAt } : {}),
      });
    },
    [botDispatch],
  );

  const fetchCheckout = useCallback(async (sessionId: string, expiresAt?: string | null) => {
    try {
      const payload = await getCheckoutPayload(sessionId);
      applyCheckoutPayload(payload, expiresAt);
    } catch (e) {
      console.error('[BotSession] fetchCheckout', e);
    }
  }, [applyCheckoutPayload]);

  /** Load an existing session into the live chat panel. Used both on initial
   *  mount and when switching tabs. Resets the chat state so we don't leak
   *  messages from the previous session. */
  const loadSessionIntoChat = useCallback(
    async (sessionId: string): Promise<boolean> => {
      try {
        const session = await loadSession(sessionId);
        botDispatch({ type: 'RESET_BOT' });
        chatDispatch({ type: 'RESET' });
        botDispatch({ type: 'SET_SESSION', sessionId: session.id, status: session.status });

        const historyMsgs: UIMessage[] = (session.messages ?? [])
          .filter(m => m.role === 'user' || m.role === 'assistant')
          .map(m => ({
            id: String(m.id),
            role: m.role as 'user' | 'assistant',
            text: m.content,
            timestamp: m.date_created,
          }));
        chatDispatch({ type: 'SET_MESSAGES', messages: historyMsgs });

        // Cache the label for the tab strip so it survives switching away.
        // Prefer BE-derived session.title (set from first user message); fall
        // back to the first user message text in the loaded history.
        const titleSeed =
          (session.title && session.title.trim()) ||
          historyMsgs.find((m) => m.role === 'user')?.text;
        if (titleSeed) {
          botDispatch({
            type: 'SET_TAB_TITLE',
            sessionId: session.id,
            title: titleSeed.slice(0, 40),
          });
        }

        if (session.context?.current_stage) {
          chatDispatch({
            type: 'SET_CURRENT_STAGE',
            stage: session.context.current_stage as any,
          });
        }

        const lang = toBotSearchLanguage(currentLang);
        if (session.context?.search?.language !== lang) {
          await updateSessionContext(session.id, {
            search: { ...(session.context?.search ?? {}), language: lang },
          });
        }

        await refreshSelections(session.id);

        const lc = (session as any).metadata?.last_checkout;
        if (lc?.expires_at && Date.parse(lc.expires_at) > Date.now()) {
          await fetchCheckout(session.id, lc.expires_at);
        }

        // Keep the single-session-key in sync so legacy hooks that read it
        // still find the active session.
        localStorage.setItem(sKey, session.id);
        return true;
      } catch (e) {
        console.error('[BotSession] loadSessionIntoChat', e, sessionId);
        return false;
      }
    },
    [botDispatch, chatDispatch, refreshSelections, fetchCheckout, sKey, currentLang],
  );

  /** Create a fresh session, append a tab for it, and switch to it. Returns
   *  the new session id on success. */
  const createTab = useCallback(async (): Promise<string | null> => {
    chatDispatch({ type: 'SET_LOADING', loading: true });
    try {
      let anonId = localStorage.getItem('sky360_bot_anon_id');
      if (!anonId) { anonId = crypto.randomUUID(); localStorage.setItem('sky360_bot_anon_id', anonId); }

      const decodeAgentIdFromJwt = (): number | undefined => {
        try {
          const token = localStorage.getItem('agent-admin-token');
          if (!token) return undefined;
          const payloadB64 = token.split('.')[1];
          if (!payloadB64) return undefined;
          const padded = payloadB64.padEnd(payloadB64.length + ((4 - (payloadB64.length % 4)) % 4), '=');
          const json = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
          const payload = JSON.parse(json);
          const id = Number(payload?.agent_id);
          return Number.isFinite(id) && id > 0 ? id : undefined;
        } catch {
          return undefined;
        }
      };
      const agentUserId = decodeAgentIdFromJwt();

      const session = await createSession({
        app: 'website',
        website_id: websiteId ? Number(websiteId) : undefined,
        agent_user_id: agentUserId,
        membership_user_id: membershipUser?.id,
        anonymous_id: agentUserId || membershipUser ? undefined : anonId,
      });

      botDispatch({ type: 'RESET_BOT' });
      chatDispatch({ type: 'RESET' });
      botDispatch({ type: 'SET_SESSION', sessionId: session.id, status: session.status });
      localStorage.setItem(sKey, session.id);

      const lang = toBotSearchLanguage(currentLang);
      await updateSessionContext(session.id, {
        search: { language: lang },
        ...(membershipUser
          ? { user_profile: { name: membershipUser.name, email: membershipUser.email } }
          : {}),
      });

      setTabs(prev => [...prev, { id: session.id, title: session.title }]);
      setActiveTabId(session.id);
      return session.id;
    } catch (e) {
      console.error('[BotSession] createTab', e);
      return null;
    } finally {
      chatDispatch({ type: 'SET_LOADING', loading: false });
    }
  }, [sKey, websiteId, membershipUser, currentLang, botDispatch, chatDispatch]);

  /** Mount-time bootstrap: rehydrate tabs from localStorage, validate active
   *  session loads, fall back to creating a fresh tab if everything is
   *  empty / stale. */
  const initSession = useCallback(async (): Promise<string | null> => {
    chatDispatch({ type: 'SET_LOADING', loading: true });
    try {
      const persisted = readPersistedTabs(tKey);
      // Legacy fallback: if we have no tabs list but the old single-session
      // key has a session id, seed the tab strip from it.
      if (persisted.tabs.length === 0) {
        const legacyId = localStorage.getItem(sKey);
        if (legacyId) {
          persisted.tabs = [{ id: legacyId }];
          persisted.activeId = legacyId;
        }
      }

      if (persisted.tabs.length > 0) {
        const targetId =
          persisted.activeId && persisted.tabs.some(t => t.id === persisted.activeId)
            ? persisted.activeId
            : persisted.tabs[0].id;

        const ok = await loadSessionIntoChat(targetId);
        if (ok) {
          setTabs(persisted.tabs);
          setActiveTabId(targetId);
          return targetId;
        }
        // Active tab failed to load — drop it and try the next, or fall
        // through to creating a fresh one.
        const remaining = persisted.tabs.filter(t => t.id !== targetId);
        if (remaining.length > 0) {
          const nextId = remaining[0].id;
          const ok2 = await loadSessionIntoChat(nextId);
          if (ok2) {
            setTabs(remaining);
            setActiveTabId(nextId);
            return nextId;
          }
        }
        // Everything stale: wipe and start fresh.
        localStorage.removeItem(tKey);
        localStorage.removeItem(sKey);
      }
    } catch (e) {
      console.error('[BotSession] initSession', e);
    } finally {
      chatDispatch({ type: 'SET_LOADING', loading: false });
    }

    // No usable tabs — create one.
    return createTab();
  }, [tKey, sKey, chatDispatch, createTab, loadSessionIntoChat]);

  /** Switch to an already-open tab. */
  const switchTab = useCallback(async (sessionId: string) => {
    if (sessionId === activeTabId) return;
    chatDispatch({ type: 'SET_LOADING', loading: true });
    try {
      const ok = await loadSessionIntoChat(sessionId);
      if (ok) setActiveTabId(sessionId);
    } finally {
      chatDispatch({ type: 'SET_LOADING', loading: false });
    }
  }, [activeTabId, chatDispatch, loadSessionIntoChat]);

  /** Close a tab. Archives its session and falls back to a sibling tab; if
   *  the closed tab was the only one, immediately creates a fresh tab. */
  const closeTab = useCallback(async (sessionId: string) => {
    try { await archiveSession(sessionId); } catch { /* idempotent */ }

    const nextTabs = tabs.filter(t => t.id !== sessionId);
    setTabs(nextTabs);
    botDispatch({ type: 'CLEAR_TAB_TITLE', sessionId });

    if (sessionId === activeTabId) {
      if (nextTabs.length > 0) {
        await switchTab(nextTabs[nextTabs.length - 1].id);
      } else {
        // Last tab gone — start fresh so the strip is never empty.
        await createTab();
      }
    }
  }, [tabs, activeTabId, switchTab, createTab, botDispatch]);

  /** Back-compat with callers that still use the "New chat" semantics
   *  (the IoAdd icon in DrawerBotWidget when tabs aren't rendered, plus any
   *  external integrations). Maps onto createTab. */
  const startNewChat = useCallback(async () => {
    return createTab();
  }, [createTab]);

  return {
    initSession,
    startNewChat,
    refreshSelections,
    fetchCheckout,
    applyCheckoutPayload,
    // Tab API
    tabs,
    activeTabId,
    createTab,
    switchTab,
    closeTab,
  };
}
